/**
 * `ArtifactPreview` — generic structured-output card used by tools that
 * return an artifact-shaped payload (anything with `title` + `data` or
 * a `kind` discriminator). Flint doesn't have a real artifacts module
 * in V1.0.x — this is the visual shell so we can wire one through the
 * moment a tool starts producing it.
 *
 * Mirrors Studio's `DocumentPreview` / `ArtifactUpdateBadge` slot.
 */
import { FileTextIcon } from "lucide-react";
import { cn } from "@flint/ui";

export interface ArtifactPreviewProps {
  title: string;
  description?: string | undefined;
  kind?: "document" | "table" | "chart" | "kpi" | "unknown" | undefined;
  body?: unknown;
  className?: string | undefined;
}

export function ArtifactPreview({
  title,
  description,
  kind = "unknown",
  body,
  className,
}: ArtifactPreviewProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-line bg-gradient-to-br from-paper to-bg/40 px-3 py-3 text-xs shadow-sm",
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <div className="grid size-8 shrink-0 place-items-center rounded-md bg-sky-50 text-sky-700">
          <FileTextIcon className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-ink">{title}</p>
          {description ? (
            <p className="mt-0.5 truncate text-xs text-ink-2">{description}</p>
          ) : null}
        </div>
        <span className="rounded-full bg-bg/70 px-2 py-0.5 text-xs uppercase tracking-wide text-ink-2">
          {kind}
        </span>
      </div>
      {body !== undefined && body !== null ? (
        <pre className="mt-3 max-h-48 overflow-auto rounded bg-bg/60 p-2 font-mono text-xs leading-5 text-ink">
          {typeof body === "string" ? body : JSON.stringify(body, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}
